import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, UserPlus, Trash2 } from "lucide-react";

const inputCls = "h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]";

function RoleBadge({ role }) {
  const isAdmin = role === "super_admin";
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${isAdmin ? "border-[#E30613]/20 bg-[#E30613]/10 text-[#FF6069]" : "border-white/10 bg-white/5 text-neutral-300"}`}>
      {isAdmin ? "Super Admin" : "Installer"}
    </span>
  );
}

const EMPTY = { business_name: "", contact_name: "", email: "", phone: "", address: "", role: "installer", status: "active", password_mode: "email", password: "" };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const createUser = async () => {
    if (!form.business_name.trim() || !form.contact_name.trim() || !form.email.trim()) { toast.error("Business, contact and email are required."); return; }
    if (form.password_mode === "temp" && !form.password.trim()) { toast.error("Enter a temporary password."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", form);
      toast.success(`${data.user.business_name} created${data.set_password_email_sent ? " — set-password email sent" : ""}`);
      setAddOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to create user"); }
    finally { setSaving(false); }
  };

  const deleteUser = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/users/${toDelete.id}`);
      toast.success(data.message || "User deleted");
      setToDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete user"); }
    finally { setDeleting(false); }
  };

  return (
    <DashboardLayout title="Users">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Access Control</p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">All users</h2>
          <p className="mt-1 text-sm text-neutral-500">Every account with access to SGBINOXE Care.</p>
        </div>
        <button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-2 self-start rounded-lg bg-[#E30613] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#FF1C2A]" data-testid="add-user-btn">
          <UserPlus className="h-4 w-4" /> Add User
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-[#141414]" data-testid="users-table">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/[0.06] text-xs uppercase tracking-wider text-neutral-500">
                  <th className="px-6 py-4 font-medium">Name</th>
                  <th className="px-6 py-4 font-medium">Email</th>
                  <th className="px-6 py-4 font-medium">Business</th>
                  <th className="px-6 py-4 font-medium">Role</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                  <th className="px-6 py-4 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-white/[0.04] transition-colors hover:bg-white/[0.03]" data-testid={`user-row-${u.id}`}>
                    <td className="px-6 py-4 font-medium text-white">{u.contact_name}</td>
                    <td className="px-6 py-4 text-neutral-300">{u.email}</td>
                    <td className="px-6 py-4 text-neutral-400">{u.business_name}</td>
                    <td className="px-6 py-4"><RoleBadge role={u.role} /></td>
                    <td className="px-6 py-4 capitalize text-neutral-400">{u.status.replace("_", " ")}</td>
                    <td className="px-6 py-4 text-right">
                      {u.id !== me?.id && (
                        <button onClick={() => setToDelete(u)} className="inline-flex items-center gap-1.5 rounded-lg border border-white/[0.1] px-3 py-1.5 text-xs font-medium text-[#FF6069] hover:bg-[#E30613]/10" data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add user modal */}
      <Dialog open={addOpen} onOpenChange={(o) => !saving && setAddOpen(o)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/[0.08] bg-[#141414] text-white" data-testid="add-user-modal">
          <DialogHeader><DialogTitle>Add User</DialogTitle><DialogDescription className="text-neutral-500">Create a new installer or admin account.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label className="text-neutral-300">Business Name *</Label><Input value={form.business_name} onChange={(e) => set("business_name")(e.target.value)} className={inputCls} data-testid="user-business" /></div>
              <div className="space-y-2"><Label className="text-neutral-300">Contact Name *</Label><Input value={form.contact_name} onChange={(e) => set("contact_name")(e.target.value)} className={inputCls} data-testid="user-contact" /></div>
              <div className="space-y-2"><Label className="text-neutral-300">Email *</Label><Input type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} className={inputCls} data-testid="user-email" /></div>
              <div className="space-y-2"><Label className="text-neutral-300">Phone</Label><Input value={form.phone} onChange={(e) => set("phone")(e.target.value)} className={inputCls} data-testid="user-phone" /></div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-neutral-300">Role</Label>
                <Select value={form.role} onValueChange={set("role")}>
                  <SelectTrigger className={inputCls} data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
                    <SelectItem value="installer">Installer</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-neutral-300">Password Setup</Label>
                <Select value={form.password_mode} onValueChange={set("password_mode")}>
                  <SelectTrigger className={inputCls} data-testid="user-password-mode"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
                    <SelectItem value="email">Email set-password link</SelectItem>
                    <SelectItem value="temp">Set a temporary password</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.password_mode === "temp" && (
              <div className="space-y-2"><Label className="text-neutral-300">Temporary Password *</Label><Input type="text" value={form.password} onChange={(e) => set("password")(e.target.value)} placeholder="Min 8 chars, incl. upper/lower/number" className={inputCls} data-testid="user-temp-password" /></div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setAddOpen(false)} disabled={saving} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05]">Cancel</button>
            <button onClick={createUser} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="create-user-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create User"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!toDelete} onOpenChange={(o) => !deleting && !o && setToDelete(null)}>
        <DialogContent className="border-white/[0.08] bg-[#141414] text-white" data-testid="delete-user-modal">
          <DialogHeader><DialogTitle>Delete user?</DialogTitle><DialogDescription className="text-neutral-500">This permanently removes {toDelete?.business_name}'s account. Their past certificates and records are retained for audit.</DialogDescription></DialogHeader>
          <DialogFooter>
            <button onClick={() => setToDelete(null)} disabled={deleting} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05]">Cancel</button>
            <button onClick={deleteUser} disabled={deleting} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="confirm-delete-user">{deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete User"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
